// Copyright 2022 Sorama B.V.
import { autoinject, observable } from "aurelia-framework";
import { NavModel, Router } from "aurelia-router";
import { Link } from "controls/selector/Selector";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";
import { IWorkflowMode } from "./IWorkflowMode";
import { WorkflowModes } from "./WorkflowModes";

@autoinject
export class WorkflowModeService {
    private static readonly workflowModeServiceGroupKey = "WorkflowModeService";
    private static readonly selectedWorkflowModeKey = "SelectedWorkflowMode";

    public readonly workflowModes: IWorkflowMode[] = [
        WorkflowModes.AcousticDesignCycleLite,
        WorkflowModes.AcousticDesignCycle,
        WorkflowModes.IndustrialFixed,
        WorkflowModes.IndustrialHandheld,
        WorkflowModes.FullWFM,
    ];
    public readonly extraUrl: Link = { text: "Need help?", url: "/WorkflowModeSelection" };

    // The workflow mode selection should always be reflecting the state in the userSettingsService(local storage), if not yet set, it is undefined
    // Automatically syncs with the user's local storage
    @observable
    public selectedWorkflowMode: IWorkflowMode;

    private selectedWorkflowModeChanged(newValue: IWorkflowMode, oldValue: IWorkflowMode): void {
        if (newValue != oldValue) {
            this.syncSelectedWorkflowModeToStorage();
            this.applyWorkflowMode();

            // Refresh page when user changes workflow mode
            if (oldValue && newValue) {
                window.location.reload();
            }
        }
    }

    public get workflowModeIsSelected(): boolean {
        return this.selectedWorkflowMode != undefined;
    }

    // Force a sync with the user's local storage
    public async syncSelectedWorkflowModeFromStorage(): Promise<IWorkflowMode> {
        try {
            const workflowModeName = await this.userSettingsService.get(
                WorkflowModeService.workflowModeServiceGroupKey,
                WorkflowModeService.selectedWorkflowModeKey
            );
            this.selectedWorkflowMode = this.userSettingsStringToWorkflow(workflowModeName);
        } catch (error) {
            this.selectedWorkflowMode = undefined;
        }

        this.applyWorkflowMode();
        return this.selectedWorkflowMode;
    }

    public async resetSelectedWorkflowModeInStorage() {
        this.selectedWorkflowMode = undefined;
    }

    constructor(
        private readonly userSettingsService: IUserSettingsService,
        private readonly router: Router
    ) {
        this.syncSelectedWorkflowModeFromStorage();
    }

    private async syncSelectedWorkflowModeToStorage() {
        if (!this.selectedWorkflowMode) return;
        await this.userSettingsService.set(
            WorkflowModeService.workflowModeServiceGroupKey,
            WorkflowModeService.selectedWorkflowModeKey,
            this.selectedWorkflowMode.name
        );
    }

    private userSettingsStringToWorkflow(workflowModeName: string): IWorkflowMode {
        if (workflowModeName == "") return undefined;

        const workflowMode = this.workflowModes.find((mode) => mode.name == workflowModeName);
        if (!workflowMode) throw new Error(`Workflow mode with name '${workflowModeName}' is not a workflow`);
        return workflowMode;
    }

    // We want to apply this to the router as a whole and not only the navBar, so we set it from here
    private applyWorkflowMode() {
        this.router.navigation.forEach((nav) => {
            if (this.workflowModeIsSelected) {
                nav.config.settings.disableNavBar = !this.selectedWorkflowMode.shouldShowWorkflow(nav.config.name);
            } else {
                nav.config.settings.disableNavBar = true;
            }
        });

        this.router.navigation.sort((a: NavModel, b: NavModel) => {
            return (a.order as number) - (b.order as number);
        });
    }
}
